import { useEffect, useRef, RefObject } from 'react'
import clsx from 'clsx'

import { getLinesContainerPadding, getGreenLineWidth } from './Wizard.helpers'

export type Step = {
  id: number | string
  label: string
  complete?: boolean
}

export interface WizardTimelineProps {
  steps: Array<Step>
  className?: string
  current: number | string
}

const GREEN_LINE_STYLE = { top: -10, height: 4 }
const LINES_CONTAINER_STYLE = { height: 2 }

function WizardProgress({
  current,
  stepsContainerRef,
}: {
  current: number | string
  stepsContainerRef: RefObject<HTMLOListElement>
}) {
  const greenLineRef = useRef<HTMLSpanElement>(null)
  const linesContainerRef = useRef<HTMLDivElement>(null)

  // TODO: recalcule on window resize
  useEffect(() => {
    if (!greenLineRef.current || !linesContainerRef.current) {
      return
    }

    const linesContainer = linesContainerRef.current as HTMLElement
    const stepsContainer = stepsContainerRef.current as HTMLElement

    const { paddingLeft, paddingRight } = getLinesContainerPadding(stepsContainer)

    linesContainerRef.current.style.paddingLeft = paddingLeft
    linesContainerRef.current.style.paddingRight = paddingRight

    greenLineRef.current.style.width = getGreenLineWidth(stepsContainer, linesContainer)
  }, [current, stepsContainerRef])

  return (
    <div
      ref={linesContainerRef}
      className={clsx('relative h-1 z-0 w-full')}
      style={LINES_CONTAINER_STYLE}
    >
      <hr
        className={clsx('inline-block bg-neutral-light z-0 border-t w-full border-neutral-light')}
      />
      <div className="relative">
        <span
          ref={greenLineRef}
          className={clsx('inline-block absolute transition-all w-0 h-1  bg-primary-500')}
          style={GREEN_LINE_STYLE}
        />
      </div>
    </div>
  )
}

function WizardTimelineEntry({
  current,
  complete,
  index,
  label,
}: Step & { index: number; current: boolean }) {
  return (
    <li
      className={clsx('rg-event relative flex flex-col items-center', {
        'is-current': current,
        'is-complete': complete,
      })}
    >
      <span
        className={clsx(
          'inline-flex items-center justify-center w-8 h-8 text-sm font-bold border-2 rounded-full mb-1',
          {
            'bg-neutral-lightest border-primary-500 text-black': current && !complete,
            'bg-primary-500 border-primary-500 text-neutral-lightest': complete,
            'bg-neutral-lightest border-neutral-light text-neutral-light': !current && !complete,
          }
        )}
      >
        {index + 1}
      </span>

      <span
        className={clsx('text-sm hidden sm:inline-block truncate', {
          'font-normal': !current && complete,
          'font-semibold text-black': current,
          'font-normal text-neutral': !current && !complete,
        })}
      >
        {/* TODO: improve style here to prevent layout shift due to different
        font weights when navigating from one step to another. */}
        {label}
      </span>
    </li>
  )
}

function WizardTimeline({ className, current, steps }: WizardTimelineProps) {
  const stepsContainerRef = useRef<HTMLOListElement>(null)

  return (
    <div className="relative w-full px-8 pt-8 pb-12 bg-neutral-lightest">
      <WizardProgress current={current} stepsContainerRef={stepsContainerRef} />
      <ol
        ref={stepsContainerRef}
        className={clsx('rg-events flex justify-between relative', className)}
      >
        {(steps || []).map((step, index) => {
          const { id } = step

          return (
            <WizardTimelineEntry
              data-testid="wizard-timeline-entry"
              key={id}
              index={index}
              current={current === id}
              {...step}
            />
          )
        })}
      </ol>
    </div>
  )
}

export default WizardTimeline
